import { catchError, of } from 'rxjs';

import { DateTime } from 'luxon';

import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { Paginator } from 'primeng/paginator';
import { toSignal } from '@angular/core/rxjs-interop';

import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';
import { CurrencyAmountComponent } from '@cxc/ui-kit/components/currency-amount';

import {
  LENDER_POSITION_LABELS,
  LenderPosition,
} from '@models/operation/lender-participation.model';

import { CurrencyState } from '@services/state/currency-state/currency.state';

import { facilityRoute } from '@utils/trust-route';
import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import { CompanyApi } from '@services/API/companies/company-api/company-api.service';

/** Una etiqueta de la compañía, como la devuelve companies-api. */
export interface FacilityTag {
  name: string;
}

/** Un renglón de la tabla: una facility, con todo lo que la fila muestra. */
export interface FacilityRow {
  trustNumber: string;
  /** El originador en negrita; el fideicomiso va debajo, en mono. */
  originator: string;
  /** `undefined` cuando la facility todavía no tiene fideicomiso. */
  route: string | undefined;
  role: string | undefined;
  participationPct: number | null | undefined;
  masterServicer: string | null | undefined;
  reportedAt: string | undefined;
  outdated: boolean;
  cadence: string | null | undefined;
  tags: FacilityTag[];
  facilitySize: number;
  commitment: number | null | undefined;
  aumPct: number | undefined;
  coLenders: string[];
}

/**
 * Paso 5 del tab Portfolio: **cada número de arriba, facility por facility**
 * (CA3, 4, 5, 6, 9 y 13 de LEN-2663).
 *
 * **Un renglón por fideicomiso, no por tramo.** El servidor ya fusiona los
 * tramos antes de devolver `core_metrics.operations[]`, así que la tabla
 * muestra lo que el payload trae. El CA9 pide un renglón por tramo y eso
 * necesita backend que todavía no existe: está en LEN-3301.
 *
 * **Ninguna facility desaparece.** Sin rol, sin %, sin cadencia, con reporte
 * viejo o sin reportes: sigue en la tabla y sigue contando en los totales. Lo
 * que cambia es que el hueco se nombra. *Outdated* informa, no excluye.
 */
@Component({
  selector: 'facilities-table',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    RouterModule,
    Paginator,
    SelectableWidget,
    CurrencyAmountComponent,
  ],
  templateUrl: './facilities-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacilitiesTable {
  private state = inject(OverviewResumeState);
  private companyApi = inject(CompanyApi);

  currency = inject(CurrencyState).displayCurrency;

  /**
   * 10 por página. El CA13 pide además que **un filtro nuevo vuelva a la
   * primera** -- eso llega con los filtros, en LEN-2665: hoy no hay ninguno
   * que pueda cambiar la lista. Lo que sí hace esto es no quedarse más allá
   * de la última página.
   */
  readonly pageSize = 10;
  private _page = signal(0);

  /**
   * Las etiquetas viven en la compañía, no en la operación, y **son
   * mutables**: por eso no viajan denormalizadas en el payload del Overview
   * (LEN-3279). Se cruzan acá por `company_id`, con **una sola** llamada por
   * carga.
   *
   * Ese listado topa en `size=100`. La respuesta trae `total`, así que el
   * recorte **se detecta** -- ver `tagsAreComplete`. Importa decirlo: una
   * celda vacía se lee como "esta compañía no tiene etiquetas", que es una
   * afirmación, no una ausencia.
   */
  private companies = toSignal(
    // `toSignal` **relanza el error al leer la señal**, así que sin esto un
    // 500 de companies-api no deja la columna TAGS vacía: deja la tabla
    // entera sin renderizar. El enriquecimiento no puede matar al dato.
    this.companyApi.getCompanies().pipe(catchError(() => of(undefined))),
    { initialValue: undefined }
  );

  /**
   * `false` cuando el listado de compañías vino recortado: entonces una celda
   * vacía no significa "sin etiquetas", significa "no sabemos".
   */
  tagsAreComplete = computed(() => {
    const page = this.companies();
    if (!page) return false;
    return (page.items?.length ?? 0) >= (page.total ?? 0);
  });

  private tagsByCompany = computed(() => {
    const index = new Map<string, FacilityTag[]>();
    for (const company of this.companies()?.items ?? []) {
      const id = company?.id ?? company?._id;
      if (!id) continue;
      index.set(String(id), company.tags ?? []);
    }
    return index;
  });

  /** El corte del payload: contra él se decide si una facility está atrasada. */
  private cutOff = computed(() => this.state.resume()?.reported_at);

  /**
   * El denominador del % OF AUM sale del payload, no de sumar los renglones.
   * Si el servidor filtrara o ajustara algo, una suma propia haría que los
   * porcentajes de la tabla no cuadren con el KPI de arriba, y nadie se
   * enteraría.
   */
  private portfolioDebt = computed(
    () => this.state.coreMetrics()?.outstanding_debt ?? 0
  );

  /** Lo que el resume sabe de cada fideicomiso, indexado para el cruce. */
  private resumeByTrust = computed(() => {
    // `name` es `string | null` en el modelo: un originador sin nombre es
    // dato real, no un caso imposible.
    const index = new Map<
      string,
      {
        reported_at?: string;
        originators?: { name: string | null }[];
        lenders?: { name: string | null; entity_id?: string | null }[];
      }
    >();
    for (const operation of this.state.operations() ?? []) {
      if (!index.has(operation.trust_number)) index.set(operation.trust_number, operation);
    }
    return index;
  });

  rows = computed<FacilityRow[]>(() => {
    const resume = this.resumeByTrust();
    const tags = this.tagsByCompany();
    const debt = this.portfolioDebt();
    const cutOff = this.cutOff();
    const lender = this.state.coreMetrics()?.lender;

    return (this.state.coreMetrics()?.operations ?? []).map(op => {
      const extra = resume.get(op.trust_number);
      const originators = extra?.originators ?? [];

      return {
        trustNumber: op.trust_number,
        originator: originators[0]?.name?.trim() || op.alias,
        // Sin fideicomiso no hay ficha que abrir: texto plano, sin enlace ni
        // foco de teclado. Es un no funcional explícito de accesibilidad.
        route: facilityRoute(op.trust_number),
        // `priority_position` es la llave del enum: la celda muestra
        // *Bilateral*, no `SOLE_LENDER`. Mismo mapa que usa Insights para
        // los cortes del mismo Overview.
        role: op.priority_position
          ? (LENDER_POSITION_LABELS[op.priority_position as LenderPosition] ??
            op.priority_position)
          : undefined,
        participationPct: op.participation_pct,
        masterServicer: op.master_admin,
        reportedAt: extra?.reported_at,
        outdated: this.isOutdated(extra?.reported_at, cutOff),
        cadence: op.reporting_periodicity,
        tags: op.company_id ? (tags.get(op.company_id) ?? []) : [],
        facilitySize: op.total_credit_amount,
        commitment: op.committed_amount,
        aumPct: debt > 0 ? ((op.outstanding_debt ?? 0) / debt) * 100 : undefined,
        // **Límite conocido: se compara por nombre.** El modelo avisa que el
        // backend acota la visibilidad por `entity_id` y no por `name`,
        // porque *"dos entidades pueden compartir nombre comercial"*
        // (`operation.model.ts`). El payload del Overview no dice cuál es la
        // entidad del lender que está mirando, sólo su nombre, así que hoy no
        // hay con qué comparar. Con homónimos esto puede ocultar un co-lender
        // real o dejar al propio adentro.
        coLenders: (extra?.lenders ?? [])
          .map(entry => entry.name?.trim())
          .filter((name): name is string => !!name && name !== lender),
      };
    });
  });

  lastPage = computed(() =>
    Math.max(0, Math.ceil(this.rows().length / this.pageSize) - 1)
  );

  /**
   * Sólo aparece si hay más de una página. Esconderlo en modo Export es parte
   * de LEN-2932, que es la HU que trae ese modo: este componente todavía no
   * recibe de nadie si está activo.
   */
  showsPaginator = computed(() => this.rows().length > this.pageSize);

  /**
   * Nunca más allá de la última: si la lista se achica —un filtro, otro
   * lender— la página en la que estabas puede dejar de existir, y quedarse
   * ahí muestra una tabla vacía que parece un error.
   */
  currentPage = computed(() => Math.min(this._page(), this.lastPage()));

  visibleRows = computed(() => {
    const start = this.currentPage() * this.pageSize;
    return this.rows().slice(start, start + this.pageSize);
  });

  goToPage(page: number): void {
    this._page.set(Math.max(0, Math.min(page, this.lastPage())));
  }

  /** Atrasada: reportó antes del corte que el Overview está mostrando. */
  private isOutdated(reportedAt?: string, cutOff?: string): boolean {
    if (!reportedAt || !cutOff) return false;
    return DateTime.fromISO(reportedAt) < DateTime.fromISO(cutOff).startOf('day');
  }
}
